/**
 * Transient confirmations — "DeCA generada", "Clave revocada".
 *
 * Announced in a polite live region so the confirmation is not visual-only; the
 * durable facts (a new document number, a URL to copy) always also land in the
 * screen itself, because a toast is not somewhere to keep information.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { Icon } from './Icon.tsx'

export type ToastTone = 'success' | 'error' | 'info'

interface Toast {
  id: number
  tone: ToastTone
  message: string
}

interface ToastApi {
  show: (message: string, tone?: ToastTone) => void
  success: (message: string) => void
  error: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  /**
   * The timers still to fire, so an unmount can clear them. A state update on an unmounted tree does
   * nothing in a browser, but under test the timer fired after jsdom was gone, where React's priority
   * check reads `window.event` and throws: a production dispatch failed on it with every test green.
   */
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of pending) clearTimeout(timer)
      pending.clear()
    }
  }, [])

  const show = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = nextId++
    setToasts((current) => [...current, { id, tone, message }])
    const timer = setTimeout(() => {
      timers.current.delete(timer)
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, 6000)
    timers.current.add(timer)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message: string) => show(message, 'success'),
      error: (message: string) => show(message, 'error'),
    }),
    [show],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            <Icon
              name={toast.tone === 'success' ? 'checkCircle' : toast.tone === 'error' ? 'alert' : 'info'}
              size={18}
            />
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

/**
 * The toasts where there are any, and null where nothing provides them — a field rendered on its
 * own, as its tests render it. For a notice that is a courtesy, never the only word on an outcome.
 */
export function useToastIfAny(): ToastApi | null {
  return useContext(ToastContext)
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside <ToastProvider>')
  return context
}
